/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICartState } from '@msdyn365-commerce/global-state';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Find the product in array of products considering product id.
 * @param productId - The searched product id.
 * @param products - The array of products in which search is executed.
 * @returns - The found product.
 */
export function getProductByProductId(productId: number | undefined, products?: SimpleProduct[]): SimpleProduct | undefined {
    if (productId !== undefined && ArrayExtensions.hasElements(products)) {
        return products.find(product => {
            return productId === product.RecordId;
        });
    }

    return undefined;
}

/**
 * Find the product in array of products considering product id and product behavior's warehouse.
 * @param productId - The searched product id.
 * @param products - The array of products in which search is executed.
 * @param warehouseId - Warehouse id of cartLine.
 * @param cartState - The cart start in case cart line has no warehouse.
 * @returns - The found product.
 */
export function getProductByProductIdAndWarehouse(
    productId: number | undefined,
    products?: SimpleProduct[],
    warehouseId?: string,
    cartState?: ICartState
): SimpleProduct | undefined {
    const targetWarehouseValue = warehouseId ?? cartState?.cart.WarehouseId;

    if (productId !== undefined && ArrayExtensions.hasElements(products)) {
        return products.find(product => {
            return productId === product.RecordId && product.Behavior?.InventoryLocationId === targetWarehouseValue;
        });
    }

    return undefined;
}
